/**
 * kc-image-state — tracks load / error state for every <img class="kc-img">.
 *
 * Every image emitted by snippets/kc-image.liquid receives the `kc-img`
 * class. The matching CSS in snippets/kc-css-variables.liquid shows:
 *   - A shimmer skeleton while the image hasn't loaded yet.
 *   - The real image (skeleton cleared) once `is-loaded` is set.
 *   - A subtle placeholder icon when `is-error` is set.
 *
 * This script does five things:
 *   1) Capture-phase document listeners for `load` and `error` (those
 *      events don't bubble, but capture works). They flip the class
 *      on the target <img>.
 *   2) On DOM-ready, scan every existing `img.kc-img` and stamp the
 *      correct class — covers images that finished loading before this
 *      script ran (cached, preloaded LCP, server-rendered above-the-fold).
 *   3) A MutationObserver on document.body catches images added later
 *      by carousel hydration, variant Section Rendering API swaps, app
 *      embeds, or any other dynamic source.
 *   4) On `error`, schedules up to MAX_RETRIES retries with exponential
 *      backoff. Recovers from transient network failures (flaky wifi,
 *      CDN hiccups) without hammering URLs that are genuinely broken.
 *   5) Listens for the browser's `online` event and retries all currently
 *      errored kc-imgs once connectivity is restored — covers "wifi
 *      dropped briefly" scenarios.
 *
 * Safe to load with `defer` — none of the work needs to happen before
 * first paint; the CSS already shows the skeleton.
 */
(function () {
  'use strict';

  // 1x1 transparent SVG. On error we swap the img's src to this so the
  // browser stops showing its native broken-image glyph; the CSS
  // background (placeholder icon) becomes the visible content.
  var ERROR_FALLBACK_SRC =
    'data:image/svg+xml;utf8,' +
    '<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 1 1%22></svg>';

  // Retry budget per image. After this many failed attempts we stop
  // (until `online` fires, which resets the counter).
  var MAX_RETRIES = 2;
  var RETRY_DELAYS_MS = [1000, 3000]; // exponential-ish backoff per retry.

  function isKcImg(node) {
    return (
      node &&
      node.nodeType === 1 &&
      node.tagName === 'IMG' &&
      node.classList &&
      node.classList.contains('kc-img')
    );
  }

  function getRetryCount(img) {
    return parseInt(img.dataset.kcImgRetries || '0', 10) || 0;
  }

  function setRetryCount(img, n) {
    img.dataset.kcImgRetries = String(n);
  }

  function captureOriginal(img) {
    // Idempotent — we only ever save the URLs the FIRST time the image
    // errors, so subsequent retries restore to the truly-original URLs
    // (not to the 1x1 fallback we swapped in).
    if (img.dataset.kcImgOriginalSrc != null) return;
    if (img.src) img.dataset.kcImgOriginalSrc = img.src;
    var srcset = img.getAttribute('srcset');
    if (srcset) img.dataset.kcImgOriginalSrcset = srcset;
    var sizes = img.getAttribute('sizes');
    if (sizes) img.dataset.kcImgOriginalSizes = sizes;
  }

  function restoreOriginal(img) {
    // Order matters: sizes + srcset must be set BEFORE src so the
    // browser re-runs responsive selection when src triggers the
    // (re-)fetch.
    if (img.dataset.kcImgOriginalSizes != null) {
      img.setAttribute('sizes', img.dataset.kcImgOriginalSizes);
    }
    if (img.dataset.kcImgOriginalSrcset != null) {
      img.setAttribute('srcset', img.dataset.kcImgOriginalSrcset);
    }
    if (img.dataset.kcImgOriginalSrc != null) {
      img.src = img.dataset.kcImgOriginalSrc;
    }
  }

  function scheduleRetry(img) {
    var count = getRetryCount(img);
    if (count >= MAX_RETRIES) return;
    var delay = RETRY_DELAYS_MS[count] || RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
    setRetryCount(img, count + 1);
    setTimeout(function () {
      // Image may have been removed from the DOM in the meantime
      // (carousel re-render, variant swap, etc.). If so, nothing to do.
      if (!img.isConnected) return;
      // If something else has already marked it loaded, skip.
      if (img.classList.contains('is-loaded')) return;
      // Re-arm: clear the error class so a successful load this time
      // can transition to `is-loaded`. Then restore the original URL —
      // the browser will re-fetch. A new failure will re-enter markError
      // → scheduleRetry until the cap is hit.
      img.classList.remove('is-error');
      restoreOriginal(img);
    }, delay);
  }

  function markLoaded(img) {
    // Keep the error state terminal *within a retry cycle* — replacing
    // src with the 1x1 transparent SVG fires a synthetic `load` event
    // that we don't want to count as success.
    if (img.classList.contains('is-error')) return;
    if (img.classList.contains('is-loaded')) return;
    img.classList.add('is-loaded');
  }

  function markError(img) {
    if (img.classList.contains('is-error')) return;
    // Save the working URLs BEFORE we swap them out for the placeholder.
    captureOriginal(img);
    img.classList.add('is-error');
    // Drop srcset/sizes so the browser doesn't pick another responsive
    // URL when we swap src — we want the broken-image glyph gone.
    if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
    if (img.hasAttribute('sizes')) img.removeAttribute('sizes');
    try {
      img.src = ERROR_FALLBACK_SRC;
    } catch (_) {
      // Setting src can throw in extreme edge cases (e.g. CSP), ignore.
    }
    scheduleRetry(img);
  }

  function checkInitialState(img) {
    // `complete` is true once the browser has finished trying to load
    // (success OR failure). `naturalWidth > 0` means success.
    if (!img.complete) return;
    if (img.naturalWidth > 0) {
      markLoaded(img);
    } else {
      markError(img);
    }
  }

  function retryAllErrored() {
    var errored = document.querySelectorAll('img.kc-img.is-error');
    for (var i = 0; i < errored.length; i++) {
      var img = errored[i];
      // Reset the per-image counter so this attempt counts as a fresh
      // cycle — connectivity has changed, the previous failure is no
      // longer evidence the URL is broken.
      setRetryCount(img, 0);
      img.classList.remove('is-error');
      restoreOriginal(img);
    }
  }

  function scan(root) {
    var imgs = root.querySelectorAll('img.kc-img');
    for (var i = 0; i < imgs.length; i++) checkInitialState(imgs[i]);
  }

  function attach() {
    document.addEventListener(
      'load',
      function (e) {
        if (isKcImg(e.target)) markLoaded(e.target);
      },
      true
    );

    document.addEventListener(
      'error',
      function (e) {
        if (isKcImg(e.target)) markError(e.target);
      },
      true
    );

    // Retry every errored image when the browser regains connectivity.
    // Per-image setTimeout retries cover transient CDN hiccups; this
    // covers the user briefly losing wifi.
    window.addEventListener('online', retryAllErrored);

    scan(document);

    if (typeof MutationObserver !== 'function') return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue;
          if (isKcImg(node)) {
            checkInitialState(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
